import { createFx, hex, type FxInstance, type PixelFrame } from "pixelfx";
import * as torch from "pixelfx/fx/fire/torch";
import { ARENA_ART_W, ART_FLOOR, ART_H } from "../shared/screen";

/** Литейня, временный фон в сетке арта 1200×540: стена с арками и кладкой, жёлоб с металлом выше голов, пол. */
const mix = (a: number, b: number, t: number): number => {
  const channel = (shift: number): number => Math.round(((a >>> shift) & 255) * (1 - t) + ((b >>> shift) & 255) * t);
  return (0xff000000 | (channel(16) << 16) | (channel(8) << 8) | channel(0)) >>> 0;
};

/** Детерминированный шум кладки: 0..1 по координатам кирпича. */
const grain = (x: number, y: number): number => ((((x * 73856093) ^ (y * 19349663)) >>> 0) % 1000) / 1000;

/**
 * Фон приглушён и темнее бойцов (правила читаемости, docs/design/art-pipeline.md): яркость стены и пола ≤ 8 %,
 * яркое — только жёлоб под потолком.
 */
export function foundryBackground(): Uint32Array {
  const out = new Uint32Array(ARENA_ART_W * ART_H);
  const top = hex("#060508");
  const low = hex("#150d11");
  const pillar = hex("#0b080c");
  const mortar = hex("#070508");
  const metal = [hex("#3a1208"), hex("#8a300c"), hex("#c8581a"), hex("#e89a4a")];
  const stone = [hex("#0e0b0d"), hex("#151013"), hex("#1d171a")];
  const gutterY = 80;
  for (let y = 0; y < ART_H; y++) {
    for (let x = 0; x < ARENA_ART_W; x++) {
      let color = mix(top, low, Math.min(1, y / ART_FLOOR));
      const arch = x % 300;
      const archTop = 120 + Math.round(Math.abs(arch - 150) ** 2 / 200);
      const inPillar = arch < 30 || arch > 270;
      if (y < ART_FLOOR && (inPillar || y < archTop)) {
        const row = Math.floor(y / 15);
        const brick = Math.floor((x + (row % 2) * 20) / 40);
        color = y % 15 === 0 || (x + (row % 2) * 20) % 40 === 0 ? mortar : mix(pillar, low, grain(brick, row) * 0.35);
      }
      const glow = Math.abs(y - gutterY);
      if (glow <= 3) color = metal[glow <= 1 ? 2 : 1] ?? color;
      else if (glow <= 22 && y > gutterY) color = mix(color, metal[0] ?? color, (23 - glow) / 46);
      if (glow <= 1 && (x * 7 + 3) % 61 < 3) color = metal[3] ?? color;
      if (y >= ART_FLOOR) {
        const depth = y - ART_FLOOR;
        const seam = depth % 16 === 0 || (x + Math.floor(depth / 16) * 37) % 90 === 0;
        color =
          depth < 2
            ? (stone[2] ?? color)
            : seam
              ? (stone[0] ?? color)
              : mix(stone[1] ?? color, stone[2] ?? color, grain(Math.floor(x / 90), Math.floor(depth / 16)) * 0.5);
      }
      out[y * ARENA_ART_W + x] = color;
    }
  }
  return out;
}

export class ArenaDecor {
  private readonly torches: FxInstance[];

  constructor() {
    // За краями арены, выше голов и тусклее: не спорят с бойцами за взгляд
    this.torches = [22, ARENA_ART_W - 22].map((x, index) =>
      createFx(torch, { x, y: ART_FLOOR - 230, seed: 11 + index, light: 0.3 }),
    );
  }

  update(dt: number): void {
    for (const fx of this.torches) fx.update(dt);
  }

  draw(frame: PixelFrame): void {
    for (const fx of this.torches) {
      fx.draw(frame, "back");
      fx.draw(frame, "front");
    }
  }
}
